import type { ReactNode } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { cn } from "@/lib/cn";
import type { Kpi, Row } from "@/mock";
import { fmt, formatDelta, formatKpi } from "@/lib/format";
import { Card } from "./Card";
import { Eyebrow, ProvenanceTag } from "./text";
import { Sparkline, Bars } from "./charts";

/**
 * Headline number card — one number, mono eyebrow, quiet provenance, a
 * sparkline. Coral is reserved for the single focal metric (design.md).
 */
export function StatCard({ kpi, className }: { kpi: Kpi; className?: string }) {
  const up = kpi.trend === "up";
  const Trend = up ? TrendingUp : TrendingDown;
  return (
    <Card className={cn("flex flex-col gap-3 p-4", className)}>
      <div className="flex items-start justify-between">
        <Eyebrow>{kpi.label}</Eyebrow>
        <ProvenanceTag source={kpi.source} />
      </div>
      <div className="flex items-end justify-between gap-3">
        <div
          className={cn(
            "font-semibold leading-none tracking-tight",
            "text-[30px]",
            kpi.focal ? "text-accent" : "text-ink-strong",
          )}
        >
          {formatKpi(kpi.value, kpi.unit)}
        </div>
        <div
          className={cn(
            "mb-0.5 inline-flex items-center gap-1 font-mono text-[12px]",
            kpi.focal ? "text-accent" : "text-muted",
          )}
        >
          <Trend className="h-3.5 w-3.5" strokeWidth={2} />
          {formatDelta(kpi.delta)}
        </div>
      </div>
      <Sparkline data={kpi.series} color={kpi.focal ? "accent" : "ink"} height={34} />
    </Card>
  );
}

/** Titled rank list (top pages, sources, channels…) built on Bars. */
export function RankList({
  title,
  rows,
  accentTop,
  footer,
  className,
}: {
  title?: ReactNode;
  rows: Row[];
  accentTop?: boolean;
  footer?: ReactNode;
  className?: string;
}) {
  return (
    <Card className={cn("p-5", className)}>
      {title && (
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-[14px] font-semibold tracking-tight text-ink-strong">{title}</h3>
          {footer}
        </div>
      )}
      <Bars
        data={rows.map((r) => ({ label: r.label, value: r.value, sub: r.sub }))}
        accentTop={accentTop}
      />
    </Card>
  );
}

/** Minimal columnar table for event-style data. */
export function DataTable({
  columns,
  rows,
  className,
}: {
  columns: { key: string; label: string; align?: "left" | "right"; mono?: boolean }[];
  rows: Record<string, ReactNode>[];
  className?: string;
}) {
  return (
    <div className={cn("overflow-x-auto", className)}>
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr className="border-b border-hairline">
            {columns.map((c) => (
              <th
                key={c.key}
                className={cn(
                  "eyebrow whitespace-nowrap pb-2 font-normal",
                  c.align === "right" ? "text-right" : "text-left",
                )}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-hairline/60 last:border-0">
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={cn(
                    "py-2.5 align-middle",
                    c.align === "right" ? "text-right" : "text-left",
                    c.mono ? "font-mono text-[12px] text-muted" : "text-ink",
                  )}
                >
                  {r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export { fmt };
